import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Award, ArrowLeft, Trophy, Flame, Heart, Calendar,
  Target, CheckCircle, Star, Edit3, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HealthPassport() {
  const { user } = useAuth();
  const [passport, setPassport] = useState(null);
  const [badges, setBadges] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIntentionEditor, setShowIntentionEditor] = useState(false);
  const [intention, setIntention] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const [passportRes, badgesRes] = await Promise.all([
        fetch(`${API}/api/fitness/passport`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/fitness/badges`)
      ]);

      if (passportRes.ok) {
        const data = await passportRes.json();
        setPassport(data);
        setIntention(data.legacy_intention || '');
      }
      if (badgesRes.ok) {
        const data = await badgesRes.json();
        setAllBadges(data.badges);
      }

      // Get user's earned badges
      const userBadges = await fetch(`${API}/api/badges/user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (userBadges.ok) {
        const data = await userBadges.json();
        setBadges(data.badges?.filter(b => b.category === 'fitness') || []);
      }
    } catch (err) {
      console.error('Error fetching passport:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveIntention = async () => {
    if (!intention.trim()) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/fitness/passport`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ legacy_intention: intention })
      });

      if (res.ok) {
        setPassport(p => ({ ...p, legacy_intention: intention }));
        setShowIntentionEditor(false);
        toast.success('Legacy intention saved!');
      }
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-amber-50/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-12">
            <Award className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-stone-800 mb-2">Your Health Passport</h2>
            <p className="text-stone-600 mb-6">Log in to track your fitness journey and earn badges.</p>
            <Link to="/login">
              <Button className="bg-rose-600 hover:bg-rose-700">Log In</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }

  const streakProgress = Math.min((passport?.current_streak_days || 0) / 30 * 100, 100);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-amber-50/30" data-testid="health-passport">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-600 to-orange-700 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/fitness" className="inline-flex items-center text-white/80 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to LegacyFit
          </Link>
          
          <div className="flex items-start gap-4">
            <div className="p-4 bg-white/20 rounded-xl">
              <Award className="h-10 w-10" />
            </div>
            <div>
              <Badge className="mb-2 bg-white/20 text-white border-0">Global Health Passport</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">Your Legacy Journey</h1>
              <p className="text-amber-100">
                Issued: {passport?.passport_issued_date ? new Date(passport.passport_issued_date).toLocaleDateString() : 'Today'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="text-center">
            <CardContent className="py-6">
              <Trophy className="h-8 w-8 text-amber-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-stone-800">{passport?.total_sessions_completed || 0}</div>
              <p className="text-sm text-stone-600">Workouts</p>
            </CardContent>
          </Card>
          <Card className="text-center">
            <CardContent className="py-6">
              <Flame className="h-8 w-8 text-orange-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-stone-800">{passport?.current_streak_days || 0}</div>
              <p className="text-sm text-stone-600">Day Streak</p>
            </CardContent>
          </Card>
          <Card className="text-center">
            <CardContent className="py-6">
              <Heart className="h-8 w-8 text-rose-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-stone-800">{passport?.legacy_moments_submitted?.length || 0}</div>
              <p className="text-sm text-stone-600">Legacy Moments</p>
            </CardContent>
          </Card>
          <Card className="text-center">
            <CardContent className="py-6">
              <Star className="h-8 w-8 text-purple-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-stone-800">{passport?.stamps?.length || 0}</div>
              <p className="text-sm text-stone-600">Badges</p>
            </CardContent>
          </Card>
        </div>

        {/* Legacy Intention */}
        <Card className="mb-8 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-amber-600" />
                <CardTitle className="text-lg">Legacy Intention</CardTitle>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowIntentionEditor(true)}>
                <Edit3 className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {passport?.legacy_intention ? (
              <p className="text-stone-700 text-lg italic">"{passport.legacy_intention}"</p>
            ) : (
              <div className="text-center py-4">
                <p className="text-stone-500 mb-3">Who are you staying healthy for?</p>
                <Button variant="outline" onClick={() => setShowIntentionEditor(true)}>
                  Set Your Intention
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Streak Progress */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-500" />
              30-Day Streak Challenge
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-stone-600">Current: {passport?.current_streak_days || 0} days</span>
              <span className="text-stone-600">Goal: 30 days</span>
            </div>
            <Progress value={streakProgress} className="h-4" />
          </CardContent>
        </Card>

        {/* Badges */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Your Badges</CardTitle>
            <CardDescription>PROOF credentials you've earned</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {allBadges.map(badge => {
                const earned = passport?.stamps?.some(s => s.badge_id === badge.id);
                return (
                  <div 
                    key={badge.id}
                    className={`p-4 rounded-xl text-center ${
                      earned ? 'bg-green-50 border-2 border-green-300' : 'bg-stone-100 opacity-50'
                    }`}
                  >
                    {earned ? (
                      <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    ) : (
                      <Star className="h-8 w-8 text-stone-300 mx-auto mb-2" />
                    )}
                    <p className={`font-medium text-sm ${earned ? 'text-green-700' : 'text-stone-500'}`}>
                      {badge.name}
                    </p>
                    <p className="text-xs text-stone-500 mt-1">{badge.description}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Legacy Moments */}
        {passport?.legacy_moments_submitted?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-rose-500" />
                Recent Legacy Moments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {passport.legacy_moments_submitted.slice(-5).reverse().map((moment, idx) => (
                  <div key={`item-${idx}`} className="p-4 bg-stone-50 rounded-lg">
                    <p className="text-sm text-stone-500 mb-1 italic">"{moment.prompt}"</p>
                    <p className="text-stone-700">{moment.response}</p>
                    <p className="text-xs text-stone-400 mt-2">
                      {new Date(moment.date).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Intention Editor Dialog */}
      <Dialog open={showIntentionEditor} onOpenChange={setShowIntentionEditor}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Your Legacy Intention</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-stone-600">
              Who are you staying healthy for? What do you want to be able to do in 10 years?
            </p>
            <Textarea
              value={intention}
              onChange={(e) => setIntention(e.target.value)}
              placeholder="I'm staying healthy for..."
              className="min-h-[120px]"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowIntentionEditor(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveIntention}
                disabled={saving || !intention.trim()}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
